import { CreateAttempt } from '@/common/types/session';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getCumulativeMorphemes,
  getMorphemesByIds,
  getWordLevel,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getMorphMaster,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { CardFeedback } from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { calculateProgress } from '@/game/gameHelpers';
import {
  getInitialMorphemeOptions,
  playMorphMasterGameInstructionsAudioBatch,
} from '@/game/MorphMaster/morphMasterHelpers';
import MinigameCardSlot from '@/game/prefabs/StudentGame/MinigameCardSlot';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';

export class MorphMasterController extends MinigameController {
  get state() {
    return GameInstance.instance.morphMaster;
  }

  override startRound() {
    const { generalGameState } = GameInstance.instance;

    if (generalGameState.isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }

    const wordChain = generalGameState.gameData.morphMaster || [];
    if (!wordChain) {
      console.error('No word chain found');
      return;
    }

    let wordToCreate = getMorphMaster().wordToCreate;
    if (!wordToCreate) {
      wordToCreate = wordChain[0];
      if (!wordToCreate) {
        console.error('No word to create found');
        return;
      }
    }

    const newBaseWordIndex = wordChain.indexOf(wordToCreate);
    const newBaseWord = wordChain[newBaseWordIndex % wordChain.length];
    const nextWordIndex = wordChain.indexOf(wordToCreate) + 1;
    const nextWord = wordChain[nextWordIndex % wordChain.length]; // Loop back if at the end

    GameInstance.instance.updateMorphMaster({
      baseWord: newBaseWord,
      wordToCreate: nextWord,
    });

    this.setMorphMasterGameBoard();
  }

  setMorphMasterGameBoard() {
    const morphMasterState = this.state;
    const baseWord = morphMasterState.baseWord;
    const wordToCreate = morphMasterState.wordToCreate;

    if (!baseWord || !wordToCreate) {
      console.error('No base word or word to create found');
      return;
    }

    const baseWordMorphemes = getMorphemesByIds([...baseWord.morphemes]);
    const wordToCreateMorphemes = getMorphemesByIds([
      ...wordToCreate.morphemes,
    ]);

    const level = GameInstance.instance.getShouldPlayCustomLevel()
      ? Math.max(getWordLevel(baseWord.id), getWordLevel(wordToCreate.id))
      : getStudent().level;
    const cumulativeMorphemes = getMorphemesByIds(
      getCumulativeMorphemes(level, true /* affixes only */),
    );

    GameInstance.instance.updateMorphMaster({
      studentAttempt: baseWordMorphemes.map((morpheme) => ({
        id: uuidv4(),
        morpheme,
      })),
      options: getInitialMorphemeOptions(
        baseWordMorphemes,
        wordToCreateMorphemes,
        cumulativeMorphemes,
        getStudent().settings.distractorCardsEnabled,
      ).map((morpheme) => ({
        id: uuidv4(),
        morpheme,
      })),
    });
  }

  override createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    const dealtCards = getMorphMaster().options;
    const boardCards = getMorphMaster().studentAttempt;
    const wordToCreate = getMorphMaster().wordToCreate;
    if (!wordToCreate) {
      console.error('No word to create found');
      return { dealtCards: [], boardCards: [] };
    }

    const cardConfig = {
      dealtCards: dealtCards?.map((data) => {
        const newCard = new StudentGameCard(minigameScene, 0, 0);
        minigameScene.initializeNewMinigameCard(newCard, data, 'hand');
        return newCard;
      }),
      boardCards: boardCards
        ?.map((data) => {
          const newCard = new StudentGameCard(minigameScene, 0, 0);

          // Move the card to the index that reflects its place in the word to create.
          const nextWordMorphemes = getMorphemesByIds(wordToCreate.morphemes);
          const indexOfMorphemeInWordToCreate = nextWordMorphemes.findIndex(
            (m) => m.spelling === data.morpheme?.spelling,
          );

          // If there's already a card at the index, just go into the first available slot.
          const cardAlreadyAtIndex =
            this.minigame.getBoardDropSite().layoutGroup?.cards[
              indexOfMorphemeInWordToCreate
            ] instanceof StudentGameCard;

          const firstAvailableSlot = this.minigame
            .getBoardDropSite()
            .layoutGroup?.cards.findIndex(
              (card: any) => card instanceof MinigameCardSlot,
            );

          if (firstAvailableSlot === -1) {
            console.error('No available slots found for card', data);
            return null;
          }

          const index =
            indexOfMorphemeInWordToCreate !== -1 && !cardAlreadyAtIndex
              ? indexOfMorphemeInWordToCreate
              : firstAvailableSlot;
          minigameScene.initializeNewMinigameCard(
            newCard,
            data,
            'board',
            index,
          );
          return newCard;
        })
        .filter((c) => c !== null),
    };

    this.minigame.getBoardDropSite().layoutGroup?.updateLayout('instant');
    return cardConfig;
  }

  override async playAudioForRound() {
    const wordChain = getGeneralGameState().gameData.morphMaster;
    if (!wordChain) {
      console.error('No word chain found');
      return;
    }
    const baseWord = getMorphMaster().baseWord;
    if (!baseWord) {
      console.error('No base word found');
      return;
    }
    const nextWord = getMorphMaster().wordToCreate;
    if (!nextWord) {
      console.error('No next word found');
      return;
    }
    const wordSegmentationEnabled =
      getStudent().settings.wordSegmentationEnabled;
    const audioDelay = getStudent().settings.audio.audioDelay;
    await playMorphMasterGameInstructionsAudioBatch(
      baseWord,
      nextWord,
      wordSegmentationEnabled,
      audioDelay,
    );
  }

  override getMaxBoardCards(): number {
    const wordToCreate = getMorphMaster().wordToCreate;
    const baseWord = getMorphMaster().baseWord;
    if (wordToCreate && baseWord) {
      return Math.max(
        getMorphemesByIds(wordToCreate.morphemes).length,
        getMorphemesByIds(baseWord.morphemes).length,
      );
    } else {
      console.error('No word to create found');
      return 0;
    }
  }

  override handleMinigameCardDropped(data: {
    card: StudentGameCard;
    dropSiteName: string;
    targetIndex: number;
  }) {
    super.handleMinigameCardDropped(data);
    // Update the max cards on the board.
    const wordToCreate = getMorphMaster().wordToCreate;
    const baseWord = getMorphMaster().baseWord;
    if (wordToCreate && baseWord) {
      const newMaxCards = Math.max(
        getMorphemesByIds(wordToCreate.morphemes).length,
        this.minigame.getBoardDropSite().layoutGroup?.getCardCount() || 0,
      );
      this.minigame.getBoardDropSite().layoutGroup?.setMaxCards(newMaxCards);
      this.minigame.setSubmissionPossible(
        this.minigame.getBoardDropSite().layoutGroup?.getCardCount() ===
          newMaxCards,
      );
    }
  }

  override async checkMinigameAnswer() {
    const currentActivityId = getSession().currentActivity?.id;
    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    const { wordToCreate } = this.state;
    if (!wordToCreate) {
      console.warn('No word to create');
      return;
    }

    const studentAttempt = this.minigame.getInput();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const student = getStudent();
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'morphmaster',
      studentAnswer: studentAttempt,
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
      correctAnswer: wordToCreate.spelling,
      correctAnswerId: wordToCreate.id,
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);
    const isCorrect = studentAttempt === wordToCreate.spelling;

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(isCorrect);

    if (isCorrect) {
      // The answer was correct. Push the word to words completed.
      GameInstance.instance.updateMorphMaster({
        wordsCompleted: [
          ...getMorphMaster().wordsCompleted,
          wordToCreate.spelling,
        ],
      });
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: true,
      });
      const updatedGeneralGameState = getGeneralGameState();
      const updatedMorphMasterState = getMorphMaster();
      const newProgress = calculateProgress(
        updatedGeneralGameState,
        updatedMorphMasterState,
        100,
      );
      GameInstance.instance.updateGeneralGameState({
        progress: newProgress,
      });
      // Move to the next word in the chain
      const generalGameState = getGeneralGameState();
      const wordChain = generalGameState.gameData.morphMaster;
      if (!wordChain) {
        console.error('No word chain found');
        return;
      }
      const newBaseWordIndex = wordChain.indexOf(wordToCreate);
      const newBaseWord = wordChain[newBaseWordIndex % wordChain.length];
      const nextWordIndex = wordChain.indexOf(wordToCreate) + 1;
      const nextWord = wordChain[nextWordIndex % wordChain.length]; // Loop back if at the end
      GameInstance.instance.updateMorphMaster({
        baseWord: newBaseWord,
      });
      const morphMasterState = getMorphMaster();
      const currentWord = morphMasterState.baseWord;
      if (!currentWord) {
        console.error('No current word found');
        return;
      }
      const baseWordMorphemes = getMorphemesByIds(currentWord.morphemes);
      const wordToCreateMorphemes = getMorphemesByIds(nextWord.morphemes);
      const level = GameInstance.instance.getShouldPlayCustomLevel()
        ? Math.max(getWordLevel(currentWord.id), getWordLevel(nextWord.id))
        : getStudent().level;
      const cumulativeMorphemes = getMorphemesByIds(
        getCumulativeMorphemes(level, true /* affixes only */),
      );
      GameInstance.instance.updateMorphMaster({
        studentAttempt: baseWordMorphemes.map((morpheme) => ({
          id: uuidv4(),
          morpheme,
        })),
        options: getInitialMorphemeOptions(
          baseWordMorphemes,
          wordToCreateMorphemes,
          cumulativeMorphemes,
          getStudent().settings.distractorCardsEnabled,
        ).map((morpheme) => ({
          id: uuidv4(),
          morpheme,
        })),
      });
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        forceIndividualFeedback: true,
        forceReadout: true,
      });
      this.setMorphMasterGameBoard();
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  override async playInstructions() {
    const wordToCreate = this.state.wordToCreate;
    if (!wordToCreate) return;

    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Replay word button clicked', {
        word: wordToCreate.spelling,
      });
    }

    await this.soundOutWord(wordToCreate);
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup;
    if (!boardState) {
      console.error('No board state found');
      return [];
    }
    const wordToCreate = getMorphMaster().wordToCreate;
    let morphemeIndex = 0;
    if (!wordToCreate) return [];
    const morphemes = getMorphemesByIds(wordToCreate.morphemes);

    return boardState.getMinigameCards().map((card: StudentGameCard) => {
      if (morphemeIndex >= morphemes.length) {
        return {
          card,
          isCorrect: false,
        };
      }
      const targetLetter = morphemes[morphemeIndex].spelling;

      morphemeIndex++;
      return {
        card,
        isCorrect: targetLetter === card.cardData?.morpheme?.spelling,
      };
    });
  }

  override canShowArticulatoryGestures(): boolean {
    return false;
  }

  override canShowWordSegmentation(): boolean {
    return false;
  }
}
